import { FC, useCallback, useMemo } from "react";
import { Box, Chip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import ConfigCards from "../../components/ConfigListShell/ConfigCards";
import ConfigRowActions from "../../components/ConfigListShell/ConfigRowActions";
import ConfigListStates, {
	type ListState,
} from "../../components/ConfigListShell/ConfigListStates";
import ConfigListToolbar from "../../components/ConfigListShell/ConfigListToolbar";
import ConfigTable, {
	type ConfigColumn,
} from "../../components/ConfigListShell/ConfigTable";
import {
	type ViewMode,
	useConfigList,
} from "../../components/ConfigListShell/useConfigList";
import { IAnalyte } from "../../domain/analyte.interface";
import { CupResponse, CupStatus } from "../../domain/cup.interface";
import { cupDeleteName } from "./cupDelete";
import ListFooter from "./ListFooter";
import { StatusChip } from "./pills";
import {
	CUP_CHIP_COLUMN_HEADER,
	CUP_COUNT_COLUMN_HEADER,
	analyteCatalogue,
	analyteCodes,
	analyteCount,
	cupSortValue,
	cupVendorCategories,
	cupsStateCopy,
	searchableCupFields,
	vendorFilterOptions,
} from "./cupsList";

/**
 * SIMS-8783 — the Cups tab.
 *
 * Owns its own `useConfigList`, so switching tabs unmounts it and clears its search, which is what
 * the design does (markup 1239). `view` is lifted to the container because the design shares one
 * view across both tabs (markup 1002).
 *
 * The header count and this panel's result count are different numbers on purpose (second-reader
 * diff D2): AC3's "the count shown is the total number of matches" is the RESULT count, while the
 * header states the whole catalogue and must not follow the filter.
 */
export interface CupsPanelProps {
	cups: CupResponse[];
	/** SIMS-8799 — open a cup in the editor. */
	onEdit: (cup: CupResponse) => void;
	/** SIMS-8850 — ask to delete a cup. The confirmation and the guard live in the container. */
	onDelete: (cup: CupResponse) => void;
	/** SIMS-8826 — duplicate as an inactive copy; the container owns the call and the notice. */
	onDuplicate: (cup: CupResponse) => void;
	/**
	 * The analyte catalogue, used ONLY to resolve an id-shaped `cup.analytes` payload to real
	 * names. `ICup.analytes` is typed `string[] | IAnalyteInCup[]` and the service does not
	 * transform it, so the object shape is an assumption — see `cupAnalytes`.
	 */
	analytes: IAnalyte[];
	isLoading: boolean;
	isError: boolean;
	view: ViewMode;
}

const CupsPanel: FC<CupsPanelProps> = ({
	cups,
	analytes,
	onEdit,
	onDelete,
	onDuplicate,
	isLoading,
	isError,
	view,
}) => {
	const catalogue = useMemo(() => analyteCatalogue(analytes), [analytes]);
	// Memoised: `useConfigList` keys its search memo on this function, so a fresh closure every
	// render would re-filter the whole collection each time (the same lesson as the analyte sort).
	const searchableFields = useCallback(
		(cup: CupResponse) => searchableCupFields(cup, catalogue),
		[catalogue],
	);

	const list = useConfigList<CupResponse>({
		rows: cups,
		searchableFields,
		categoriesOf: cupVendorCategories,
		sortValue: cupSortValue,
		defaultSortKey: "name",
	});

	const vendors = useMemo(() => vendorFilterOptions(cups), [cups]);

	/**
	 * The three states the hand-off requires to be visibly distinct (§7.1), plus the load failure.
	 * Order matters: an empty CATALOGUE and an empty SEARCH RESULT are different states and must
	 * never render as each other.
	 */
	const listState: ListState = isLoading
		? "loading"
		: isError
			? "load-failed"
			: cups.length === 0
				? "empty"
				: list.total === 0
					? "no-results"
					: "ready";

	// One definition for both views: the design puts the same kebab on a row and on a card, and
	// two copies would drift the moment either grew an action.
	const rowActions = (cup: CupResponse) => (
		<ConfigRowActions
			// Name AND vendor. A cup name is unique only within a vendor (SIMS-8799 AC4), so
			// `cup.name` alone gives two kebabs the same accessible name the moment two vendors
			// sell a "12 Panel" — ambiguous to a screen reader, and an ambiguous locator for the
			// tests that address it by that name.
			recordName={cupDeleteName(cup)}
			actions={[
				{
					label: "Edit cup",
					icon: <EditIcon sx={{ fontSize: "1.125rem" }} />,
					onClick: () => onEdit(cup),
				},
				{
					label: "Duplicate",
					icon: <ContentCopyIcon sx={{ fontSize: "1.125rem" }} />,
					onClick: () => onDuplicate(cup),
				},
				{
					label: "Delete",
					icon: <DeleteIcon sx={{ fontSize: "1.125rem" }} />,
					onClick: () => onDelete(cup),
					destructive: true,
				},
			]}
		/>
	);

	const columns: ConfigColumn<CupResponse>[] = [
		{
			key: "name",
			header: "Cup",
			width: "1.4fr",
			sortable: true,
			render: (cup) => (
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }} noWrap>
						{cup.name}
					</Typography>
					<Typography
						sx={{ fontSize: "0.75rem", color: "text.secondary" }}
						noWrap
					>
						{cup.description}
					</Typography>
				</Box>
			),
		},
		{
			key: "vendor",
			header: "Vendor",
			width: "180px",
			sortable: true,
			render: (cup) => (
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ fontSize: "0.8125rem" }} noWrap>
						{cup.vendor}
					</Typography>
					{/* SIMS-8962 AC6 resolves a blank display to the vendor at READ time, so
					    whatever the service returns is rendered as-is. */}
					{cup.display ? (
						<Typography
							sx={{ fontSize: "0.75rem", color: "text.secondary" }}
							noWrap
						>
							{cup.display}
						</Typography>
					) : null}
				</Box>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "120px",
			sortable: true,
			render: (cup) => <StatusChip active={cup.status === CupStatus.ACTIVE} />,
		},
		{
			key: "analyteCount",
			header: CUP_COUNT_COLUMN_HEADER,
			width: "100px",
			sortable: true,
			render: (cup) => (
				// AC5 — a count of what the cup CARRIES. Never a panel size, and never derived
				// from visit records (FR-44, C-2).
				<Typography
					sx={{ fontSize: "0.875rem" }}
					data-testid="cup-analyte-count"
				>
					{analyteCount(cup)}
				</Typography>
			),
		},
		{
			key: "analytes",
			header: CUP_CHIP_COLUMN_HEADER,
			width: "1.6fr",
			render: (cup) => (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
					{analyteCodes(cup, catalogue).map((code) => (
						<Chip
							key={code}
							size="small"
							label={code}
							sx={{ fontFamily: "monospace", fontSize: "0.625rem" }}
						/>
					))}
				</Box>
			),
		},
		{
			key: "actions",
			// Blank, matching Payers (index.tsx:494). An empty <th> does leave the kebab column
			// unlabelled to a screen reader, but ConfigTable's `header` is a plain string with no
			// visually-hidden path, so the fix is a heading on the shared table rather than a
			// visible "Actions" column on this screen alone — which would make the two config
			// screens differ for no product reason. Each kebab carries its own accessible name.
			header: "",
			width: "56px",
			sortable: false,
			align: "right",
			// SIMS-8850 — the only entry point to the delete confirmation. SIMS-8783 shipped the
			// list without it and deferred the control to this story; the editor header is a third
			// entry point that belongs to SIMS-8799 and is not wired here.
			render: (cup) => (
				<Box display="flex" justifyContent="flex-end">
					{rowActions(cup)}
				</Box>
			),
		},
	];

	const resultLabel = list.isNarrowed
		? `Showing ${list.total} of ${list.grandTotal}`
		: `${list.grandTotal} cups`;

	return (
		<>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					flexWrap: "wrap",
					py: 2,
				}}
			>
				<ConfigListToolbar
					query={list.query}
					onQueryChange={list.setQuery}
					searchPlaceholder="Search cups by name, vendor or analyte"
					status={list.status}
					onStatusChange={list.setStatus}
					// The design's cups toolbar carries vendor chips only; an empty array hides
					// the status control rather than inventing a filter no AC asks for.
					statusOptions={[]}
					categoryLabel="Vendor"
					category={list.category}
					onCategoryChange={list.setCategory}
					// SIMS-9253 — the Hi-Fi draws this filter as pills, not a select (L134-136).
					categoryVariant="chips"
					allCategoriesLabel="All vendors"
					// AC4 — derived from the data, the same way the design derives vendors. The
					// prototype hard-codes the analyte TYPE list; see Q2.
					categoryOptions={vendors.map((v) => ({ value: v, label: v }))}
				/>
				<Box sx={{ flex: 1 }} />
				<Typography
					sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
					data-testid="cups-result-count"
				>
					{resultLabel}
				</Typography>
			</Box>

			{listState !== "ready" ? (
				<ConfigListStates
					state={listState}
					view={view}
					copy={cupsStateCopy}
					query={list.query}
					onClearSearch={() => list.setQuery("")}
					onClearAllFilters={() => {
						list.setQuery("");
						list.setCategory("all");
					}}
				/>
			) : view === "table" ? (
				<ConfigTable<CupResponse>
					rows={list.visible}
					columns={columns}
					rowKey={(cup) => cup._id}
					sortKey={list.sortKey}
					sortDir={list.sortDir}
					onSort={list.toggleSort}
					onRowClick={onEdit}
					ariaLabel="Cups"
				/>
			) : (
				<ConfigCards<CupResponse>
					rows={list.visible}
					rowKey={(cup) => cup._id}
					onRowClick={onEdit}
					// SIMS-9254 — the status pill sits in the card header, before the kebab (L329).
					trailing={(cup) => (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<StatusChip active={cup.status === CupStatus.ACTIVE} />
							{rowActions(cup)}
						</Box>
					)}
					title={(cup) => cup.name}
					subtitle={(cup) => cup.display || cup.vendor}
					description={(cup) => cup.description}
					stats={(cup) => [
						// AC5 in the card view. "Visits this year" is deliberately ABSENT: the
						// design shows it, no AC asks for it and SIMS-8962 delivers no such
						// field. Question Q4 on the ticket.
						{ label: "Analytes", value: analyteCount(cup) },
						{ label: "Vendor", value: cup.vendor },
					]}
					body={(cup) => (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
							{analyteCodes(cup, catalogue).map((code) => (
								<Chip
									key={code}
									size="small"
									label={code}
									sx={{ fontFamily: "monospace", fontSize: "0.625rem" }}
								/>
							))}
						</Box>
					)}
				/>
			)}

			{listState === "ready" && (
				<ListFooter
					page={list.page}
					pageCount={list.pageCount}
					onPageChange={list.setPage}
					pageSize={list.pageSize}
					onPageSizeChange={list.setPageSize}
					pageSizeOptions={list.pageSizeOptions}
					rangeStart={list.rangeStart}
					rangeEnd={list.rangeEnd}
					total={list.total}
				/>
			)}
		</>
	);
};

export default CupsPanel;
